import React, { useEffect, useRef } from 'react';
import '../Pages/styles.css'; // Import the prefixed CSS as requested

// --- Import FontAwesomeIcon Component ---
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

// --- Import ALL Necessary Icons from Font Awesome (Updated for Cost Reduction) ---
import {
    // Core Concepts & General
    faDollarSign,       // Cost / Profitability / Finance
    faScissors,         // Cost Reduction / Cutting Costs
    faTasks,            // Operational Efficiency / Processes / Workflow
    faCheckCircle,      // Quality / Standards / Success
    faLightbulb,        // Strategy / Innovation / Solutions
    faBalanceScale,     // Balancing Cost & Quality
    // Strategic Approach (6 Points)
    faProjectDiagram,   // 1. Process Optimization
    faLaptopCode,       // 2. Technology Integration
    faUsersCog,         // 3. Resource Management (Staff, Equip, Facilities)
    faChartBar,         // 4. Performance & Functional Analysis (Data)
    faTrophy,           // 5. Benchmarking
    faSyncAlt,          // 6. Continuous Monitoring & Improvement
    // Key Features (reuse some from above)
    faBrain,            // Assimilation (Understanding)
    // faLaptopCode / faChartBar reused for Tech Analysis
    // faUsersCog reused for Resource Optimization
    faTachometerAlt,    // Performance Monitoring (Dashboard)
    // faTrophy reused for Benchmarking
    faSmileBeam,        // Patient Satisfaction Focus
    // Keep other potentially useful icons if needed
    // Icons likely unused from previous versions:
    /*
    faHandshake, faExpandArrowsAlt, faBuilding, faChartLine, faMapMarkedAlt, faBoxOpen,
    faSitemap, faUserTie, faAward, faSearchDollar, faFileInvoiceDollar, faReceipt, faStream,
    faBullhorn, faCheckCircle, faSearchLocation, faComments, faTags, faStar, faPrint,
    faMousePointer, faNewspaper, faCalendarCheck, faCrosshairs
    */
} from '@fortawesome/free-solid-svg-icons'; // Using free solid icons


// --- Intersection Observer Hooks (No changes needed in hook logic) ---
const useIntersectionObserverTitle = (options) => {
    const containerRef = useRef(null);
    const [isVisible, setIsVisible] = React.useState(false);
    const callbackFunction = (entries) => {
        const [entry] = entries;
        if (entry.isIntersecting) setIsVisible(true);
    };
    useEffect(() => {
        const observer = new IntersectionObserver(callbackFunction, options);
        const currentRef = containerRef.current;
        if (currentRef) observer.observe(currentRef);
        return () => { if (currentRef) observer.unobserve(currentRef); };
    }, [containerRef, options]);
    return [containerRef, isVisible];
};

const useIntersectionObserverElements = (options) => {
    useEffect(() => {
        const observerCallback = (entries, observer) => {
            entries.forEach((entry) => {
                if (entry.isIntersecting) {
                    const target = entry.target;
                    const parent = target.parentElement;
                    const siblings = parent ? Array.from(parent.children).filter(el => el.classList.contains('cliniqor-fade-in-up')) : [target];
                    const index = siblings.indexOf(target);
                    const staggerIndex = index >= 0 ? index : 0;

                    target.style.setProperty('--item-index', staggerIndex % 8); // Keep stagger logic
                    target.classList.add('cliniqor-visible'); // Use prefixed class
                    observer.unobserve(target);
                }
            });
        };
        const observer = new IntersectionObserver(observerCallback, options);
        const targets = document.querySelectorAll('.cliniqor-fade-in-up');
        targets.forEach(target => observer.observe(target));
        return () => targets.forEach(target => { if (target) observer.unobserve(target); });
    }, [options]);
};
// --- End Observer Hooks ---


function App() {

    // --- Observer Setup for Titles (Adjusted Refs) ---
    const titleObserverOptions = { threshold: 0.2 };
    const [title1Ref, title1Visible] = useIntersectionObserverTitle(titleObserverOptions); // Importance Section
    const [title2Ref, title2Visible] = useIntersectionObserverTitle(titleObserverOptions); // Strategic Approach (6 points)
    const [title3Ref, title3Visible] = useIntersectionObserverTitle(titleObserverOptions); // Key Features
    const [title4Ref, title4Visible] = useIntersectionObserverTitle(titleObserverOptions); // Proven Success (Optional)

    // --- Observer Setup for General Fade-In Elements ---
    useIntersectionObserverElements({ threshold: 0.1 });

    // Choose a relevant hero background image for cost/finance/efficiency
    const heroBgImage = 'https://images.unsplash.com/photo-1554224154-26032ffc0d07?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1920&q=80'; // Calculator/budgeting image

  return(
    // Use prefixed base wrapper class
    <div className="cliniqor-app-wrapper">

      {/* --- HERO SECTION --- */}
      <header
        className="cliniqor-hero-section" // Prefixed
        style={{ backgroundImage: `url(${heroBgImage})` }}
      >
        <div className="cliniqor-container"> {/* Prefixed */}
          <div className="cliniqor-hero-content"> {/* Prefixed */}
            {/* --- NEW HERO CONTENT --- */}
            <h1>Cost Reduction: Maximizing Profitability</h1>
            <p className="cliniqor-sub-heading">
              Strategic Solutions for Healthcare Providers by Cliniqor
            </p>
            <p className="cliniqor-intro-text">
              Efficient cost control is essential for long-term financial sustainability. Cliniqor offers innovative strategies to significantly reduce operational costs while maintaining high standards of patient care.
            </p>
            {/* --- END NEW HERO CONTENT --- */}
          </div>
        </div>
      </header>

      <main>
        {/* --- IMPORTANCE OF COST REDUCTION --- */}
        <section className="cliniqor-section cliniqor-section-first cliniqor-section-bg-gradient cliniqor-intro-section">
          <div className="cliniqor-container">
            <div ref={title1Ref} className={`cliniqor-section-title cliniqor-fade-in-up ${title1Visible ? 'cliniqor-visible' : ''}`}>
              <h2>The Importance of Cost Reduction in Healthcare</h2>
              <p>Equally powerful as revenue generation for profitability and viability.</p>
            </div>
             <div className="cliniqor-intro-grid"> {/* Keeping grid structure */}
                 <div className="cliniqor-intro-text-content cliniqor-fade-in-up">
                     {/* --- NEW INTRO CONTENT --- */}
                     <p>Amidst rising costs, patient demands, and regulations, optimizing expenses is key. Cost reduction strategies can achieve desired profit margins without compromising service quality.</p>
                     <p>Cliniqor helps strike the delicate balance using advanced technology, streamlined workflows, and efficient processes to reduce waste, increase productivity, and improve profitability.</p>
                     {/* --- END NEW INTRO CONTENT --- */}
                 </div>
             </div>
          </div>
        </section>

        {/* --- STRATEGIC APPROACH (Detailed Planning Section - 6 points) --- */}
        <section className="cliniqor-section cliniqor-section-bg-texture cliniqor-detailed-planning-section"> {/* Kept existing classes */}
          <div className="cliniqor-container">
             <div ref={title2Ref} className={`cliniqor-section-title cliniqor-fade-in-up ${title2Visible ? 'cliniqor-visible' : ''}`}>
              {/* --- NEW SECTION TITLE --- */}
              <h2>A Strategic Approach to Cost Reduction</h2>
               <p>A systematic, data-driven focus on operations, workflows, resources, and technology.</p>
               {/* --- END NEW SECTION TITLE --- */}
            </div>
            {/* Using Planning Items for the 6 Approach Steps */}
            <div className="cliniqor-planning-items-wrapper">
                 <div className="cliniqor-planning-item cliniqor-fade-in-up">
                    <div className="cliniqor-icon-box"><FontAwesomeIcon icon={faProjectDiagram} /></div>
                    <div className="cliniqor-content"><h4>1. Process Optimization</h4> <p>Auditing workflows to identify bottlenecks, remove redundancies, automate tasks, optimize staff allocation.</p> </div>
                 </div>
                 <div className="cliniqor-planning-item cliniqor-fade-in-up">
                    <div className="cliniqor-icon-box"><FontAwesomeIcon icon={faLaptopCode} /></div>
                    <div className="cliniqor-content"><h4>2. Technology Integration</h4> <p>Leveraging AI, ML, data analytics for automation, decision support, predicting needs, tracking KPIs.</p> </div>
                 </div>
                 <div className="cliniqor-planning-item cliniqor-fade-in-up">
                    <div className="cliniqor-icon-box"><FontAwesomeIcon icon={faUsersCog} /></div>
                    <div className="cliniqor-content"><h4>3. Resource Management</h4> <p>Optimizing staffing levels/schedules, equipment utilization/lifecycle, and facility space usage.</p> </div>
                 </div>
                 <div className="cliniqor-planning-item cliniqor-fade-in-up">
                    <div className="cliniqor-icon-box"><FontAwesomeIcon icon={faChartBar} /></div>
                    <div className="cliniqor-content"><h4>4. Performance & Functional Analysis</h4> <p>Using data analytics to assess departmental performance, patient flow, supply chain, finding inefficiencies.</p> </div>
                 </div>
                 <div className="cliniqor-planning-item cliniqor-fade-in-up">
                    <div className="cliniqor-icon-box"><FontAwesomeIcon icon={faTrophy} /></div>
                    <div className="cliniqor-content"><h4>5. Benchmarking</h4> <p>Comparing performance against industry leaders to identify best practices and improvement areas.</p> </div>
                 </div>
                  <div className="cliniqor-planning-item cliniqor-fade-in-up">
                    <div className="cliniqor-icon-box"><FontAwesomeIcon icon={faSyncAlt} /></div>
                    <div className="cliniqor-content"><h4>6. Continuous Monitoring & Improvement</h4> <p>Ongoing evaluation of implemented measures, identifying new opportunities, adapting to trends.</p> </div>
                 </div>
            </div>
          </div>
        </section>

        {/* --- KEY FEATURES OF STRATEGY (Features Section - Reused) --- */}
        <section className="cliniqor-section cliniqor-features-section cliniqor-section-bg-light">
           <div className="cliniqor-container">
              <div ref={title3Ref} className={`cliniqor-section-title cliniqor-fade-in-up ${title3Visible ? 'cliniqor-visible' : ''}`}>
                 <h2>Key Features of Cliniqor’s Cost Reduction Strategy</h2>
                 <p>Comprehensive and multifaceted for sustainable profitability.</p>
              </div>
              <div className="cliniqor-features-grid">
                    {/* --- Cards for Key Features --- */}
                    <div className="cliniqor-feature-card cliniqor-fade-in-up">
                        <div className="cliniqor-feature-card-content">
                            <div className="cliniqor-icon-wrapper"><FontAwesomeIcon icon={faBrain} /></div>
                            <h4>1. Assimilation</h4>
                            <p>Thorough understanding of unique operational needs.</p>
                        </div>
                    </div>
                    <div className="cliniqor-feature-card cliniqor-fade-in-up">
                        <div className="cliniqor-feature-card-content">
                            <div className="cliniqor-icon-wrapper"><FontAwesomeIcon icon={faChartBar} /></div> {/* Data */}
                            <h4>2. Technology-Enabled Analysis</h4>
                            <p>Data-driven insights via advanced tools and analytics.</p>
                        </div>
                    </div>
                    <div className="cliniqor-feature-card cliniqor-fade-in-up">
                        <div className="cliniqor-feature-card-content">
                            <div className="cliniqor-icon-wrapper"><FontAwesomeIcon icon={faUsersCog} /></div>
                            <h4>3. Resource Optimization</h4>
                            <p>Maximizing efficient use of staff, equipment, facilities.</p>
                        </div>
                    </div>
                    <div className="cliniqor-feature-card cliniqor-fade-in-up">
                        <div className="cliniqor-feature-card-content">
                            <div className="cliniqor-icon-wrapper"><FontAwesomeIcon icon={faTachometerAlt} /></div>
                            <h4>4. Performance Monitoring</h4>
                            <p>Continuous tracking and adjustments as needed.</p>
                        </div>
                    </div>
                     <div className="cliniqor-feature-card cliniqor-fade-in-up">
                        <div className="cliniqor-feature-card-content">
                            <div className="cliniqor-icon-wrapper"><FontAwesomeIcon icon={faTrophy} /></div>
                            <h4>5. Benchmarking</h4>
                            <p>Identifying best practices via industry comparisons.</p>
                        </div>
                    </div>
                     <div className="cliniqor-feature-card cliniqor-fade-in-up">
                        <div className="cliniqor-feature-card-content">
                            <div className="cliniqor-icon-wrapper"><FontAwesomeIcon icon={faSmileBeam} /></div>
                            <h4>6. Patient Satisfaction Focus</h4>
                            <p>Maintaining/improving care quality while reducing costs.</p>
                        </div>
                    </div>
               </div>
           </div>
        </section>

       {/* --- PROVEN SUCCESS (Simple Text Section) --- */}
        <section className="cliniqor-section"> {/* Default background */}
           <div className="cliniqor-container">
              <div ref={title4Ref} className={`cliniqor-section-title cliniqor-fade-in-up ${title4Visible ? 'cliniqor-visible' : ''}`}>
                 <FontAwesomeIcon icon={faCheckCircle} size="2x" style={{ marginRight: '15px', color: '#28a745' }} /> {/* Success Icon */}
                 <h2>Proven Success in Cost Reduction</h2>
              </div>
              <div className="cliniqor-text-content cliniqor-fade-in-up">
                   <p>Cliniqor has helped numerous healthcare organizations worldwide reduce costs and maximize profits. By focusing on efficiency, technology, and data-driven decision-making, we achieve remarkable results.</p>
                   <p>We work with hospitals to streamline operations, cut waste, improve resource allocation, and implement cost-effective technologies, helping them achieve long-term financial sustainability.</p>
              </div>
           </div>
        </section>

      </main>

      {/* --- FOOTER CTA --- */}
      <footer className="cliniqor-section cliniqor-section-narrow cliniqor-footer-cta">
        <div className="cliniqor-container cliniqor-fade-in-up">
          {/* --- UPDATED FOOTER CONTENT --- */}
          <h2>Sustainable Profit Maximization through Cost Reduction</h2>
          <p>Cliniqor offers comprehensive strategies to maximize profitability without compromising care quality. Partner with us to unlock new avenues for profitability and ensure long-term financial success.</p>
          <a href="#contact" className="cliniqor-cta-button">Request Cost Reduction Consultation</a>
          {/* --- END UPDATED FOOTER CONTENT --- */}
        </div>
      </footer>
    </div> // End cliniqor-app-wrapper
  );
}

export default App;